import { MouseEvent, ReactElement, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import { IconButton, Tooltip } from '@mui/material'
import { useListGrants } from '@api/ai-access/ai-access'
import type { AIAccessGrantListDTO } from '@api/fastAPI.schemas'
import { useSnackbar } from '@ui/snackbar'
import { HeaderBadge } from '@ui/structure'
import useHasAiPermission from 'src/hooks/useHasAiPermission'
import AIAccessPopover from './AIAccessPopover'
import { isExpired } from './utils'

/**
 * The KI-Zugriff button: badge shows the number of active grants (what the
 * AI may currently touch); the popover manages grants and shows the MCP
 * connection info. Only rendered for users with the access:ai permission --
 * the backend refuses everyone else anyway (ai_feature_not_enabled).
 */
const AIAccessWidget = (): ReactElement | null => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const { enqueueSnackbar } = useSnackbar()
  const { hasAiPermission } = useHasAiPermission()
  // The onError lives on this always-mounted observer only -- the popover's
  // Verbindung tab shares the query key and shows its own inline error; a
  // second onError would double the snackbars.
  const { data } = useListGrants<AIAccessGrantListDTO, AxiosError>({
    query: {
      refetchInterval: 120_000,
      enabled: hasAiPermission,
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'KI-Freigaben konnten nicht geladen werden'), {
          variant: 'error',
        })
      },
    },
  })
  // Same expiry predicate as the popover: the server already excludes
  // expired grants per response, this only covers renders between refetches.
  const grantCount =
    data?.grants.filter(grant => !isExpired(grant.expires_at, Date.now())).length ?? 0

  if (!hasAiPermission) return null

  const handleToggle = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(prev => (prev ? null : event.currentTarget))
  }

  return (
    <>
      <Tooltip title="KI-Zugriff">
        <IconButton
          size="small"
          sx={{ color: 'rgba(0, 0, 0, 0.54)', width: 32, height: 32 }}
          onClick={handleToggle}
          data-cy="ai-access-button"
        >
          <HeaderBadge color="primary" badgeContent={grantCount} invisible={grantCount === 0}>
            <AutoAwesomeOutlinedIcon fontSize="small" />
          </HeaderBadge>
        </IconButton>
      </Tooltip>
      {anchorEl && <AIAccessPopover anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />}
    </>
  )
}

export default AIAccessWidget
